import autobind from "autobind-decorator";
import { observer } from "mobx-react";
import React from "react";
import ReactDOM from "react-dom";
import styled, { css } from "styled-components";

import DomUtils from "@client/utils/DomUtils";
import Style from "@client/utils/Style";

import { scrollItemIntoView, Tip, updateTipStyle } from "../Dropdown";
import tipImage from "../Dropdown/images/tip";
import Input from "./Input";

const getWidth = (props: any) => {
  if (props.width) {
    return props.width - 2;
  }

  if (props.large) {
    return Style.inputSizes.large.width - 2;
  }

  return Style.inputSizes.regular.width - 2;
};
const Wrapper = styled.div<{ width?: number }>`
  position: relative;
  ${props =>
    props.width
      ? css`
          width: ${props.width}px;
        `
      : ""}
`;
const List = styled.div<{ width?: number }>`
  position: absolute;
  background: white;
  cursor: pointer;
  width: ${props => getWidth(props)}px;
  border: 1px solid ${Style.palette.grayscale[3]};
  border-radius: ${Style.borderRadius};
  z-index: 1000;
`;
const ListItems = styled.div`
  max-height: 400px;
  overflow: auto;
`;
const SearchNotFound = styled.div`
  padding: 8px;
  cursor: default;
`;
type ListItemProps = {
  selected?: boolean;
  dim?: boolean;
};
const ListItem = styled.div<ListItemProps>`
  position: relative;
  color: ${(props: any) =>
    props.selected
      ? "white"
      : props.dim
        ? Style.palette.grayscale[3]
        : Style.palette.grayscale[4]};
  ${props =>
    props.selected
      ? css`
          background: ${Style.palette.primaryHighlight};
        `
      : ""}
  ${props =>
    props.selected
      ? css`
          font-weight: ${Style.fontWeights.medium};
        `
      : ""}

  padding: 8px 16px;
  transition: all ${Style.animations.swift};

  &:first-child {
    border-top-left-radius: ${Style.borderRadius};
    border-top-right-radius: ${Style.borderRadius};
  }

  &:last-child {
    border-bottom-left-radius: ${Style.borderRadius};
    border-bottom-right-radius: ${Style.borderRadius};
  }

  &:hover {
    background: ${Style.palette.primaryHighlight};
    color: white;
  }
`;
const DuplicatedLabel = styled.div`
  display: flex;
  font-size: 11px;
  span {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
`;

type Props = {
  selectedItem?: any;
  items?: any[];
  labelField?: string;
  valueField?: string;
  className?: string;
  onChange?: (item: any) => void;
  onInputChange?: (value: string, filteredItems: any[]) => void;
  noItemsMessage?: string;
  disabled?: boolean;
  width?: number;
  dimNullValue?: boolean;
  highlight?: boolean;
};
type State = {
  showDropdownList: boolean;
  firstItemHover: boolean;
  searchValue: string;
  filteredItems: any[];
};
@observer
class AutocompleteDropdown extends React.Component<Props, State> {
  static defaultProps: Partial<Props> = {
    noItemsMessage: "No results found",
  };

  state: State = {
    showDropdownList: false,
    firstItemHover: false,
    searchValue: "",
    filteredItems: [],
  };

  buttonRef: HTMLElement | null = null;
  listRef: HTMLElement | null = null;
  listItemsRef: HTMLElement | null = null;
  tipRef: HTMLElement | null = null;
  firstItemRef: HTMLElement | null = null;
  scrollableParent: HTMLElement | null = null;
  buttonRect: ClientRect | null = null;
  itemMouseDown = false;

  UNSAFE_componentWillMount() {
    this.setState({
      filteredItems: this.props.items || [],
      searchValue: this.props.selectedItem
        ? this.getLabel(this.props.selectedItem)
        : "",
    });
  }

  componentDidMount() {
    window.addEventListener("mousedown", this.handlePageClick, false);
    const buttonRef = this.buttonRef;
    if (!buttonRef) {
      return;
    }
    this.scrollableParent = DomUtils.getScrollableParent(
      buttonRef,
    ) as HTMLElement;
    this.scrollableParent.addEventListener("scroll", this.handleScroll);
    window.addEventListener("resize", this.handleScroll);
    this.buttonRect = buttonRef.getBoundingClientRect();
  }

  UNSAFE_componentWillReceiveProps(newProps: Props) {
    this.setState({ filteredItems: this.getFilteredItems(newProps) });
  }

  UNSAFE_componentWillUpdate() {
    if (this.buttonRef)
      this.buttonRect = this.buttonRef.getBoundingClientRect();
  }

  componentDidUpdate() {
    this.updateListPosition();
  }

  componentWillUnmount() {
    window.removeEventListener("mousedown", this.handlePageClick, false);
    window.removeEventListener("resize", this.handleScroll, false);
    if (this.scrollableParent) {
      this.scrollableParent.removeEventListener(
        "scroll",
        this.handleScroll,
        false,
      );
    }
  }

  getLabel(item: any) {
    const labelField = this.props.labelField || "label";

    if (item == null) {
      return "";
    }

    return (
      (item[labelField] != null && item[labelField].toString()) ||
      item.toString()
    );
  }

  getValue(item: any) {
    const valueField = this.props.valueField || "value";

    if (item == null) {
      return null;
    }

    if (typeof item === "string") {
      return item;
    }

    return (item[valueField] != null && item[valueField].toString()) || null;
  }

  getFilteredItems(props?: Props | null, searchValue?: string): any[] {
    const useProps = props || this.props;
    const useSearch =
      searchValue === undefined ? this.state.searchValue : searchValue;
    if (!useProps.items) {
      return [];
    }
    return useProps.items.filter(i => {
      const label = this.getLabel(i).toLowerCase();
      const value = this.getValue(i) || "";
      return (
        label.indexOf(useSearch.toLowerCase()) > -1 ||
        value.indexOf(useSearch.toLowerCase()) > -1
      );
    });
  }

  @autobind
  handleScroll() {
    const buttonRef = this.buttonRef;
    if (!buttonRef) {
      return;
    }
    if (DomUtils.isElementInViewport(buttonRef, this.scrollableParent)) {
      this.buttonRect = buttonRef.getBoundingClientRect();
      this.updateListPosition();
    } else if (this.state.showDropdownList) {
      this.setState({ showDropdownList: false });
    }
  }

  @autobind
  handlePageClick() {
    if (!this.itemMouseDown) {
      this.setState({ showDropdownList: false });
    }
  }

  handleButtonClick() {
    if (this.props.disabled) {
      return;
    }

    this.setState(
      {
        showDropdownList: !this.state.showDropdownList,
        filteredItems: this.props.items || [],
      },
      () => {
        this.scrollIntoView();
      },
    );
  }

  handleItemClick(item: any) {
    this.setState({
      showDropdownList: false,
      firstItemHover: false,
      searchValue: this.getLabel(item),
      filteredItems: this.getFilteredItems(null, this.getLabel(item)),
    });

    if (this.props.onChange) {
      this.props.onChange(item);
    }
  }

  handleItemMouseEnter(index: number) {
    if (index === 0) {
      this.setState({ firstItemHover: true });
    }
  }

  handleItemMouseLeave(index: number) {
    if (index === 0) {
      this.setState({ firstItemHover: false });
    }
  }

  handleSearchInputChange(searchValue: string, isFocus?: boolean) {
    const filteredItems = isFocus
      ? this.props.items || []
      : this.getFilteredItems(null, searchValue);

    this.setState(
      {
        searchValue,
        filteredItems,
        showDropdownList: true,
      },
      () => {
        if (isFocus) {
          this.scrollIntoView();
        }
      },
    );

    if (this.props.onInputChange) {
      this.props.onInputChange(searchValue, filteredItems);
    }
  }

  scrollIntoView() {
    const itemIndex = this.state.filteredItems.findIndex(
      i => this.getValue(i) === this.getValue(this.props.selectedItem),
    );
    if (!this.listRef || !this.listItemsRef) {
      return;
    }
    scrollItemIntoView(this.listRef, this.listItemsRef, itemIndex);
  }

  updateListPosition() {
    if (
      !this.state.showDropdownList ||
      !this.listRef ||
      !this.buttonRef ||
      !document.body ||
      !this.buttonRect
    ) {
      return;
    }

    const buttonHeight = this.buttonRef.offsetHeight;
    const tipHeight = 8;
    const listTop = this.buttonRect.top + buttonHeight + tipHeight;
    let listHeight = this.listRef.offsetHeight;

    if (listTop + listHeight + 16 > window.innerHeight) {
      listHeight = window.innerHeight - listTop - 16;
    } else {
      listHeight = 400;
    }

    // If a modal is opened, body scroll is removed and body top is set to replicate scroll position
    let scrollOffset = 0;
    if (parseInt(document.body.style.top, 10) < 0) {
      scrollOffset = -parseInt(document.body && document.body.style.top, 10);
    }

    if (!this.listRef) {
      return;
    }
    this.listRef.style.top = `${
      listTop + (window.pageYOffset || scrollOffset)
    }px`;
    this.listRef.style.left = `${this.buttonRect.left + window.pageXOffset}px`;

    if (!this.listItemsRef || !this.tipRef || !this.firstItemRef) {
      return;
    }
    this.listItemsRef.style.maxHeight = `${listHeight}px`;
    updateTipStyle(this.listItemsRef, this.tipRef, this.firstItemRef);
  }

  renderItems() {
    if (this.state.filteredItems.length === 0) {
      return null;
    }

    const selectedValue = this.getValue(this.props.selectedItem);
    const duplicatedLabels: string[] = [];
    this.state.filteredItems.forEach((item, i) => {
      const label = this.getLabel(item);
      for (let j = i + 1; j < this.state.filteredItems.length; j += 1) {
        if (
          label === this.getLabel(this.state.filteredItems[j]) &&
          !duplicatedLabels.find(item2 => this.getLabel(item2) === label)
        ) {
          duplicatedLabels.push(label);
        }
      }
    });

    return (
      <ListItems
        ref={ref => {
          this.listItemsRef = ref;
        }}
      >
        {this.state.filteredItems.map((item, i) => {
          const label = this.getLabel(item);
          const value = this.getValue(item);
          const duplicatedLabel = duplicatedLabels.find(l => l === label);
          const listItem = (
            <ListItem
              key={value}
              ref={ref => {
                if (i === 0) {
                  this.firstItemRef = ref;
                }
              }}
              onMouseDown={() => {
                this.itemMouseDown = true;
              }}
              onMouseUp={() => {
                this.itemMouseDown = false;
              }}
              onMouseEnter={() => {
                this.handleItemMouseEnter(i);
              }}
              onMouseLeave={() => {
                this.handleItemMouseLeave(i);
              }}
              onClick={() => {
                this.handleItemClick(item);
              }}
              selected={value !== null && value === selectedValue}
              dim={this.props.dimNullValue && value == null}
            >
              {label}
              {duplicatedLabel ? (
                <DuplicatedLabel>
                  {" "}
                  (<span>{value || ""}</span>)
                </DuplicatedLabel>
              ) : (
                ""
              )}
            </ListItem>
          );

          return listItem;
        })}
      </ListItems>
    );
  }

  renderSearchNotFound() {
    if (
      this.state.searchValue === "" ||
      !this.props.items ||
      this.props.items.length === 0 ||
      this.state.filteredItems.length > 0
    ) {
      return null;
    }

    return (
      <ListItems>
        <SearchNotFound
          onClick={() => {
            this.setState({ showDropdownList: false });
          }}
        >
          {this.props.noItemsMessage}
        </SearchNotFound>
      </ListItems>
    );
  }

  renderList() {
    if (!this.state.showDropdownList) {
      return null;
    }

    const body: any = document.body;
    const selectedItemValue = this.getValue(this.props.selectedItem);
    const firstItemValue =
      this.state.filteredItems.length > 0
        ? this.getValue(this.state.filteredItems[0])
        : null;
    const isFirstItemSelected =
      selectedItemValue !== null && selectedItemValue === firstItemValue;

    const list = ReactDOM.createPortal(
      <List
        {...this.props}
        ref={ref => {
          this.listRef = ref;
        }}
      >
        <Tip
          ref={ref => {
            this.tipRef = ref;
          }}
          primary={this.state.firstItemHover || isFirstItemSelected}
          dangerouslySetInnerHTML={{ __html: tipImage }}
        />
        {this.renderItems()}
        {this.renderSearchNotFound()}
      </List>,
      body,
    );

    return list;
  }

  render() {
    const nullLabel =
      this.props.items && this.getValue(this.props.items[0]) === null
        ? this.getLabel(this.props.items[0])
        : "";
    const inputValue =
      this.getValue(this.props.selectedItem) === null &&
      this.state.searchValue === nullLabel
        ? ""
        : this.state.searchValue;

    return (
      <Wrapper
        className={this.props.className}
        onMouseDown={() => {
          this.itemMouseDown = true;
        }}
        onMouseUp={() => {
          this.itemMouseDown = false;
        }}
        width={this.props.width}
      >
        <Input
          width={this.props.width}
          customRef={ref => {
            this.buttonRef = ref;
          }}
          // @ts-ignore
          onMouseDown={() => {
            this.itemMouseDown = true;
          }}
          onMouseUp={() => {
            this.itemMouseDown = false;
          }}
          value={inputValue}
          onClick={() => this.handleButtonClick()}
          onChange={searchValue => {
            this.handleSearchInputChange(searchValue);
          }}
          onFocus={() => {
            this.handleSearchInputChange(this.state.searchValue, true);
          }}
          highlight={this.props.highlight}
          disabled={this.props.disabled}
        />
        {this.renderList()}
      </Wrapper>
    );
  }
}

export default AutocompleteDropdown;
